import React, { Suspense, useState } from "react";
import styled from "styled-components";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  // useSearchParams
} from "react-router-dom";

// Landing page
import Landing from "./pages/landing/Main";

// Auth pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import AcceptInvitation from "./pages/auth/AcceptInvitation";
import ForgotPassword from "./pages/auth/ForgotPassword";
import SuccessSend from "./pages/auth/SuccessSend";
import PasswordReset from "./pages/auth/PasswordReset";

// Dashboard pages
import Dashboard from "./pages/dashboard/Index";

import Tournaments from "./pages/tournametns/Index";
import Team from "./pages/tournametns/team/Index";
import Player from "./pages/tournametns/player/Index";
import Statistics from "./pages/tournametns/statistics/Index";
import Formation from "./pages/tournametns/formation/Index";
import Competition from "./pages/tournametns/competition/Index";

import SmrtSearch from "./pages/smrtSearch/index";
import SmrtPlayer from "./pages/smrtSearch/Id";
import SmrtCurrentClub from "./pages/smrtSearch/CurrentClub";
import SmrtCompare from "./pages/smrtSearch/Compare";
import ShadowTeam from "./pages/shadowTeam/Index";
import Favorites from "./pages/favorites/Index";
import Playlist from "./pages/playlist/Index";
import SharedPlaylist from "./pages/shared_playlist/Index";
import PublicSharedPlaylist from "./pages/shared/Playlist";
import Setup from "./pages/setup/Index";
import Account from "./pages/account/Index";
import ExternalVideoUpload from "./pages/externalVideoUpload/Index";
import Contact from "./pages/contact/Index";
import SequenceSearch from "./pages/sequenceSearch/Index";
import UserPlaylist from "./pages/user_playlist/index";
import VideoListUpdatedV2 from "./pages/tournametns/player/VideoListUpdatedV2";

import { BetaVersion } from "./style/SmrtSearch";
import { LoadingWrapper } from "./style/Global";
import version from "./images/version.png";
import versionBlack from "./images/versionBlack.png";
import closeIcon from "./images/ic_close_white.svg";
import closeIconBlack from "./images/ic_close_black.svg";

// import { SystemSettings } from "../SystemSettings";
// import { SystemSettings } from "./components/SystemSettings";
import {
  $apiV1,
  $apiV2,
} from "./config/api";

// 404
import Error from "./pages/error/Index";

// Privacy pages
import Terms from "./pages/terms/index";
import Privacy from "./pages/privacy/index";
import Video from "./pages/tournametns/player/Video";
// import { VideoList } from "./style/Overview";
import VideoListPage from "./pages/tournametns/player/VideoList";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PlayerProtectedRoute } from "./components/PlayerProtectedRoute";
import { AdminProtectedRoute } from "./components/AdminProtectedRoute";
import { AffiliateProtectedRoute } from "./components/AffiliateProtectedRoute";
import PlayerLayout from "./layouts/PlayerLayout";
import TeamAdminLayout from "./layouts/TeamAdminLayout";
import AffiliateLayout from "./layouts/AffiliateLayout";
import AffiliateDashboard from "./pages/affiliate/Dashboard";
import PlayerDashboard from "./pages/player/Dashboard";
import PlayerMatches from "./pages/player/Matches";
import PlayerMatchDetail from "./pages/player/MatchDetail";
import PlayerMatchVideo from "./pages/player/MatchVideo";
import PlayerEvents from "./pages/player/Events";
import PlayerEventsVideo from "./pages/player/EventsVideo";
import PlayerFavorites from "./pages/player/Favorites";
import PlayerStatistics from "./pages/player/Statistics";
import PlayerProfile from "./pages/player/Profile";
import PlayerNotMapped from "./pages/player/NotMapped";
import PlayerAccessSuspended from "./pages/player/AccessSuspended";
import TeamAdminDashboard from "./pages/team-admin/Dashboard";
import TeamAdminPlayers from "./pages/team-admin/Players";
import TeamAdminPlayerMapping from "./pages/team-admin/PlayerMapping";
import TeamAdminSettings from "./pages/team-admin/Settings";
import SubscriptionExpired from "./pages/subscription/Expired";
import SubscriptionSuccess from "./pages/subscription/Success";
import SubscriptionUpgrade from "./pages/subscription/Upgrade";
import PlayerRegister from "./pages/player/Register";
import { captureReferralFromUrl } from "./helpers/referral";
import PlayerVerifyEmail, { PlayerVerifyEmailPending } from "./pages/player/VerifyEmail";
import PlayerSetupCard from "./pages/player/SetupCard";
import PlayerPickPlayer from "./pages/player/PickPlayer";
import AdminViewAs from "./pages/player/ViewAs";
import {
  useCallback,
  useEffect,
  //  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "./store/user/userSlice";
import { VideoListUpdated } from "./pages/tournametns/player/VideoListUpdated";
import { DowloadVideoList } from "./components/downLoadVideos/DowloadVideoList";
import { getLanguages, getTranslates } from "./store/languages/languagesService";
import { clearAuthContextStorage, fetchSubscriptionStatus, getFormationInfo, readAuthContextFromStorage } from "./store/user/userService";
import { getDeviceId } from "./helpers/deviceId";
import { useLexicon } from "./hooks/useLexicon";
import HistoryTracker from "./pages/historyTracker/HistoryTracker";
import { Oval } from "react-loader-spinner";
import { invalidateUserProfile } from "./helpers/userProfile";
import { initPerfMonitor } from "./tracking/perfMonitor";

const _isLightTheme = localStorage.getItem('theme') === 'light';

const Compare = React.lazy(() => import("./pages/compare/Index"));

// Events Explorer (lazy-loaded)
const EventsExplorer = React.lazy(() => import("./pages/events-explorer/Index"));


function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [routePath, setRoutePath] = useState(location.pathname);
  // const [searchParams] = useSearchParams();

  const [expanded, setExpanded] = useState(JSON.parse(localStorage.getItem('showBetaInfo')) ?? true);
  const [forceLogout, setForceLogout] = useState(false);

  const l = useLexicon();
  const handleForceLogoutClose = useCallback(() => {
    setForceLogout(false);
  }, [navigate]);

  const handleClick = () => {
    setExpanded(!expanded);
    localStorage.setItem('showBetaInfo', JSON.stringify(!expanded));
  };

  const isPlayerOnlyRole = () => {
    const rolesRaw = localStorage.getItem("user_roles");
    if (rolesRaw) {
      try {
        const parsed = JSON.parse(rolesRaw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.length === 1 && parsed[0] === "player";
        }
      } catch (e) {
        // fall through to user_type
      }
    }
    return localStorage.getItem("user_type") === "player";
  };

  const checkUserToken = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token || token === "undefined") {
      invalidateUserProfile();
      dispatch(userActions.setToken(""));
      dispatch(userActions.clearAuthContext());
      clearAuthContextStorage();
      return;
    }
    dispatch(userActions.setToken(token));
    dispatch(userActions.setAuthContext(readAuthContextFromStorage()));
  }, [dispatch]);

  useEffect(() => {
    checkUserToken();
  }, [checkUserToken]);

  // Detect main-thread freezes (Chrome "Page Unresponsive") and report them
  // through the tracking pipeline so we can confirm/locate them in production.
  useEffect(() => {
    initPerfMonitor();
  }, []);

  useEffect(() => {
    dispatch(getLanguages());
    dispatch(getTranslates(localStorage.getItem('language') || 'en'));
    dispatch(getFormationInfo());
    if (localStorage.getItem('token')) {
      dispatch(fetchSubscriptionStatus());
    }
  }, [dispatch]);

  // Referral attribution (SMRTSTATS-351): stash ?ref= from any entry URL so
  // it survives the hop to /player/register. Runs on every navigation; a URL
  // without ?ref leaves a previously captured code intact.
  useEffect(() => {
    const captured = captureReferralFromUrl(location.search);
    // A referral link (…/?ref=CODE) lands on the marketing page, which has no
    // signup CTA — take invited, logged-out visitors straight to the register
    // form, where the "X invited you" banner picks the code up.
    const token = localStorage.getItem("token");
    if (captured && location.pathname === "/" && (!token || token === "undefined")) {
      navigate("/player/register", { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname.includes("/login")) return;
    if (isPlayerOnlyRole()) return;
    $apiV1.get("/users/check_token").catch((error) => {
      const status = error?.response?.status;
      if (status === 401 || status === 410) {
        localStorage.removeItem("token");
        invalidateUserProfile();
        checkUserToken();
      }
    });
    //code for send activity user to server
    // const path = searchParams.size ? `?${decodeURI(searchParams.toString())}` : '';
    // $apiV1.post('/users/activity/', {
    //   link_from: `https://football.smrtstats.com${routePath}${path}`,
    //   link_to: `https://football.smrtstats.com/${location.pathname}${path}`,
    //   type: 1,
    // }).finally(() => {
    //   setRoutePath(location.pathname)
    // })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (isPlayerOnlyRole()) return;
    let cancelled = false;
    let logoutTimer;
    $apiV2
      .post("/session/check", {
        device_id: getDeviceId()
      })
      .then(() => {})
      .catch((error) => {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 410) {
          setForceLogout(true);
          localStorage.removeItem("token");
          invalidateUserProfile();
          clearAuthContextStorage();
          dispatch(userActions.setToken(""));
          dispatch(userActions.clearAuthContext());
          logoutTimer = setTimeout(() => {
            navigate("/login", { replace: true });
          }, 1500);
        }
      });
    return () => {
      cancelled = true;
      clearTimeout(logoutTimer);
    };
  }, [dispatch, navigate]);

  // getDeviceId moved to src/helpers/deviceId.js with a crypto.getRandomValues
  // fallback (crypto.randomUUID is gated on secure contexts and breaks on
  // iOS Safari over plain HTTP — e.g. Tailscale/LAN dev access).

  return (
    <>
      {forceLogout && (
        <FullscreenOverlay>
          <ModalBox>
            <ModalTitle>{l(1118)}</ModalTitle>
            <ModalActions>
              <ModalButton  onClick={handleForceLogoutClose}>OK</ModalButton>
            </ModalActions>
          </ModalBox>
        </FullscreenOverlay>
      )}
      <DowloadVideoList />
      <HistoryTracker />
      <Routes>
        <Route path="/" element={<Landing />}></Route>

        {/* Privacy pages */}
        <Route path="/terms" element={<Terms />}></Route>
        <Route path="/privacy" element={<Privacy />}></Route>

        {/* Auth pages */}
        <Route path="/login" element={<Login />}></Route>
        <Route path="/register" element={<Register />}></Route>
        <Route path="/accept-invitation" element={<AcceptInvitation />}></Route>
        <Route path="/forgot-password" element={<ForgotPassword />}></Route>
        <Route path="/success" element={<SuccessSend />}></Route>
        <Route path="/password-reset" element={<PasswordReset />}></Route>
        {/* Dashboard pages */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tournaments"
          element={
            <ProtectedRoute>
              <Tournaments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/team/:id"
          element={
            <ProtectedRoute>
              <Team />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/player/:id"
          element={
            <ProtectedRoute>
              <Player />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/match/video/:id"
          element={
            <ProtectedRoute>
              <Video />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/competition/:id"
          element={
            <ProtectedRoute>
              <Competition />
            </ProtectedRoute>
          }
        />

        {/* DEPRECATED */}
        <Route
          path="/tournaments/match/video"
          element={
            <ProtectedRoute>
              {/* <VideoListPage /> */}
              <VideoListUpdated />
            </ProtectedRoute>
          }
        />
        {/* NEW VERSION WITH TOKEN */}
        <Route
          path="/tournaments/match/videos"
          element={
            <ProtectedRoute>
              <VideoListUpdatedV2 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/statistics/:id"
          element={
            <ProtectedRoute>
              <Statistics />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tournaments/formation"
          element={
            <ProtectedRoute>
              <Formation />
            </ProtectedRoute>
          }
        />

        <Route
          path="/smrt-search"
          element={
            <ProtectedRoute>
              <SmrtSearch />
            </ProtectedRoute>
          }
        />

        <Route
          path="/compare"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={(
                  <LoadingWrapper>
                    <Oval
                      height={48}
                      width={48}
                      color={_isLightTheme ? "#2363F7" : "#C8FF00"}
                      visible
                      ariaLabel="compare-loading"
                      secondaryColor={_isLightTheme ? "rgba(35, 99, 247, 0.25)" : "rgba(200, 255, 0, 0.25)"}
                      strokeWidth={4}
                      strokeWidthSecondary={4}
                    />
                  </LoadingWrapper>
                )}
              >
                <Compare />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sequence-search"
          element={
            <ProtectedRoute>
              <SequenceSearch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shadow-team"
          element={
            <ProtectedRoute>
              <ShadowTeam />
            </ProtectedRoute>
          }
        />
        <Route
          path="/smrt-explorer"
          element={
            <ProtectedRoute>
              <SmrtExplorerGuard>
                <Suspense fallback={<div>Loading...</div>}>
                  <EventsExplorer />
                </Suspense>
              </SmrtExplorerGuard>
            </ProtectedRoute>
          }
        />
        <Route path="/events-explorer" element={<Navigate to="/smrt-explorer" replace />} />
        <Route
          path="/smrt-search/:id"
          element={
            <ProtectedRoute>
              <SmrtPlayer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/smrt-search/compare"
          element={
            <ProtectedRoute>
              <SmrtCompare />
            </ProtectedRoute>
          }
        />
        <Route
          path="/smrt-search/current-club"
          element={
            <ProtectedRoute>
              <SmrtCurrentClub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/favorites"
          element={
            <ProtectedRoute>
              <Favorites />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-playlists"
          element={
            <ProtectedRoute>
              <Playlist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-playlists/:id"
          element={
            <ProtectedRoute>
              <UserPlaylist />
            </ProtectedRoute>
          }
        />
        <Route path="/shared_playlist/:id" element={<SharedPlaylist />} />
        {/* Public Player PWA share URL (SMRTSTATS-286 #4) — no auth, no
            ProtectedRoute. Anyone with the link can watch. */}
        <Route path="/shared/playlist/:token" element={<PublicSharedPlaylist />} />
        <Route
          path="/platform-setup"
          element={
            <ProtectedRoute>
              <Setup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/external-video-upload"
          element={<ProtectedRoute><ExternalVideoUpload /></ProtectedRoute>}
        />
        <Route
          path="/setup"
          element={
            <ProtectedRoute>
              <Setup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact-us"
          element={
            <ProtectedRoute>
              <Contact />
            </ProtectedRoute>
          }
        />

        {/* Player PWA routes */}
        <Route
          path="/player"
          element={
            <PlayerProtectedRoute>
              <PlayerLayout />
            </PlayerProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/player/dashboard" replace />} />
          <Route path="dashboard" element={<PlayerDashboard />} />
          <Route path="matches" element={<PlayerMatches />} />
          <Route path="matches/:id" element={<PlayerMatchDetail />} />
          <Route path="matches/:id/video" element={<PlayerMatchVideo />} />
          <Route path="events" element={<PlayerEvents />} />
          <Route path="events/video" element={<PlayerEventsVideo />} />
          <Route path="favorites" element={<PlayerFavorites />} />
          <Route path="statistics" element={<PlayerStatistics />} />
          <Route path="profile" element={<PlayerProfile />} />
          <Route path="not-mapped" element={<PlayerNotMapped />} />
        </Route>

        {/* Team admin routes */}
        <Route
          path="/team-admin"
          element={
            <AdminProtectedRoute>
              <TeamAdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/team-admin/dashboard" replace />} />
          <Route path="dashboard" element={<TeamAdminDashboard />} />
          <Route path="players" element={<TeamAdminPlayers />} />
          <Route path="players/:id/map" element={<TeamAdminPlayerMapping />} />
          <Route path="settings" element={<TeamAdminSettings />} />
        </Route>

        {/* Club admin routes — the same organization screens as team admin,
            rendered with club vocabulary (organization kind = 'club'). */}
        <Route
          path="/club-admin"
          element={
            <AdminProtectedRoute>
              <TeamAdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/club-admin/dashboard" replace />} />
          <Route path="dashboard" element={<TeamAdminDashboard />} />
          <Route path="players" element={<TeamAdminPlayers />} />
          <Route path="settings" element={<TeamAdminSettings />} />
        </Route>

        {/* A player whose organization (agency / club) was switched off —
            target of the 402 ORGANIZATION_SUSPENDED interceptor branch. */}
        <Route path="/player/access-suspended" element={<PlayerAccessSuspended />} />

        {/* Affiliate (reseller partner) routes */}
        <Route
          path="/affiliate"
          element={
            <AffiliateProtectedRoute>
              <AffiliateLayout />
            </AffiliateProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/affiliate/dashboard" replace />} />
          <Route path="dashboard" element={<AffiliateDashboard />} />
        </Route>

        {/* Subscription */}
        <Route path="/subscription/expired" element={<SubscriptionExpired />} />
        <Route path="/subscription/upgrade" element={<SubscriptionUpgrade />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />

        {/* Player self-serve onboarding (PRD §11.2-§11.4). Public — register
            and verify-email/:token are unauthenticated; setup-card requires
            the JWT minted by verify-email. */}
        <Route path="/player/register" element={<PlayerRegister />} />
        <Route path="/player/verify-email-pending" element={<PlayerVerifyEmailPending />} />
        <Route path="/player/verify-email/:token" element={<PlayerVerifyEmail />} />
        <Route
          path="/player/setup-card"
          element={
            <ProtectedRoute>
              <PlayerSetupCard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/player/onboarding/pick-player"
          element={
            <ProtectedRoute>
              <PlayerPickPlayer />
            </ProtectedRoute>
          }
        />
        {/* Admin "view-as" picker (C1) — platform admins choose which player
            to inspect in the PWA without holding the player role. */}
        <Route
          path="/player/view-as"
          element={
            <ProtectedRoute>
              <AdminViewAs />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="/error" element={<Error />}></Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {/* <BetaVersion onClick={handleClick}>
        {!expanded &&
          <img
            src={
              localStorage.getItem("theme") === "light" ? version : versionBlack
            }
            alt={version}
          />
        }
        {expanded && (
          <span>
            <img
              src={
                localStorage.getItem("theme") === "light"
                  ? closeIcon
                  : closeIconBlack
              }
              alt={closeIcon}
            />
            {l(1024)}
          </span>
        )}
      </BetaVersion> */}
    </>
  );
}

function SmrtExplorerGuard({ children }) {
  const allowed = useSelector(state => state.user.smrtExplorerAllowed);
  if (allowed === null) return <div>Loading...</div>;
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return children;
}

export default App;

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  padding: 28px 32px;
  border-radius: 12px;
  max-width: 960px;
  width: 100%;
  box-shadow: 0 15px 30px rgba(0,0,0,0.3);
  border-left: 8px solid ${(props) => props.theme.red};

  @media (max-width: 768px) {
    max-width: 640px;
    padding: 22px 24px;
    border-left-width: 6px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 18px 18px;
    border-left-width: 5px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-family: "SatoshiBold";
  font-size: 24px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const ModalActions = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 480px) {
    justify-content: center;
  }
`;

const ModalButton = styled.button`
  padding: 12px 18px;
  border: none;
  border-radius: 10px;
  background: ${(props) => props.theme.blueish3};
  color: ${(props) => props.theme.white};
  font-family: "SatoshiBold";
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);

  &:hover {
    filter: brightness(0.95);
  }

  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 15px;
  }
`;
